import { useCallback, useMemo } from 'react';
import { doc, runTransaction } from 'firebase/firestore';
import { firestore } from '../../context/FirebaseConfig';
import { PRODUCTION_EVENT_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { formatDateTime, toDate } from '../../utils/timeUtils';
/**
 * Shared instruction workflow helpers used across FeedTimestamping + RecipeDisplay.
 *
 * @param {Object} [params]
 * @param {Object|null} [params.currentUser] - Auth user with company context.
 * @param {Object|null} [params.shiftInfo] - Shift state containing active operator id.
 * @param {Object|null} [params.settings] - Client settings including users map.
 * @param {Array<Object>} [params.productionEvents=[]] - Live production event documents.
 * @param {string} [params.fallbackUserLabel='User'] - Fallback label when no operator can be resolved.
 */
export function useInstruction({ currentUser, shiftInfo, settings, fallbackUserLabel = 'User' } = {}) {
  const activeUserName = useMemo(() => {
    if (shiftInfo?.user && settings?.users && settings.users[shiftInfo.user]) {
      const user = settings.users[shiftInfo.user];
      return `${user.firstname} ${user.lastname}`;
    }
    return currentUser?.displayName || currentUser?.email || fallbackUserLabel;
  }, [shiftInfo?.user, settings?.users, currentUser?.displayName, currentUser?.email, fallbackUserLabel]);

  const formatInstructionDate = useCallback(
    (value) => formatDateTime(toDate(value), currentUser?.info?.languageCode),
    [currentUser?.info?.languageCode],
  );

  /**
   * Atomically marks an instruction event document as confirmed.
   * Targets the event document directly by its Firestore ID.
   */
  const markInstructionConfirmed = useCallback(
    async (eventDocId, instruction, userName = activeUserName) => {
      if (!eventDocId || !instruction || !userName) return;
      if (!currentUser?.company?.company) return;

      const confirmedBy = Array.isArray(instruction?.confirmed_by) ? instruction.confirmed_by : [];
      if (confirmedBy.includes(userName)) return;

      const eventDocRef = doc(firestore, PRODUCTION_EVENT_DOCUMENT_PATH(currentUser.company.company, eventDocId));

      try {
        await runTransaction(firestore, async (transaction) => {
          const eventSnap = await transaction.get(eventDocRef);
          if (!eventSnap.exists()) return;

          const existingConfirmedBy = Array.isArray(eventSnap.data()?.confirmed_by)
            ? eventSnap.data().confirmed_by
            : [];

          transaction.update(eventDocRef, {
            confirmed: true,
            confirmed_timestamp: new Date(),
            confirmed_by: Array.from(new Set([...existingConfirmedBy, userName])),
          });
        });
      } catch (error) {
        console.error('Failed to confirm instruction:', error);
        throw error;
      }
    },
    [activeUserName, currentUser?.company?.company],
  );

  return {
    activeUserName,
    formatInstructionDate,
    markInstructionConfirmed,
  };
}
